import React from 'react'
import { COLORS } from '../theme'

// Industry context for a metric.
//
// DELIBERATELY NOT A RANK. DORA 2025 retired the elite/high/medium/low tiers in
// favour of team archetypes, so there is no tier to award and a badge saying
// "elite" would be inventing one the industry removed. This renders the field
// DISTRIBUTION and lets the reader place us.
//
// It also refuses to flatten three different kinds of comparison into one look:
//   level      our number vs the field's — only where the units genuinely match
//   direction  our rate of CHANGE vs theirs — where no one publishes a level
//   context    the field's shape, with an explicit note that ours measures a
//              different span and is NOT a like-for-like
// The `note` is rendered every time, because every one of these carries a
// caveat that changes what the number means.

function Row({ children }) {
    return <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12 }}>{children}</div>
}

function Distribution({ bands, unit }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 5 }}>
            {bands.map((b) => (
                <Row key={b.band}>
                    <span style={{ color: COLORS.textMuted, minWidth: 92 }}>{b.band}</span>
                    <span
                        style={{
                            height: 5,
                            width: `${Math.min(b.share * 1.6, 100)}%`,
                            background: `${COLORS.accent}99`,
                            borderRadius: 3,
                        }}
                    />
                    <span style={{ color: COLORS.textMuted, fontSize: 11 }}>{b.share}%</span>
                </Row>
            ))}
            {unit && <span style={{ fontSize: 10.5, color: COLORS.textMuted }}>share of teams surveyed</span>}
        </div>
    )
}

export default function Benchmark({ benchmark, compact = false }) {
    if (!benchmark) return null
    const { label, distribution, compare, fieldChange, ourChange, goodDirection, ours, note, source, retrieved } =
        benchmark

    const behind =
        compare === 'direction' && typeof fieldChange === 'number' && typeof ourChange === 'number'
            ? goodDirection === 'down'
                ? ourChange > fieldChange
                : ourChange < fieldChange
            : null

    return (
        <div
            style={{
                marginTop: 12,
                paddingTop: 10,
                borderTop: `1px dashed ${COLORS.border}`,
            }}
        >
            <div
                style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: COLORS.accent,
                    marginBottom: 4,
                }}
            >
                How this compares
            </div>
            <div style={{ fontSize: 12, color: COLORS.text, marginBottom: 2 }}>{label}</div>

            {distribution && <Distribution bands={distribution} unit />}

            {ours && (
                <div style={{ fontSize: 12, color: COLORS.warning, marginTop: 6 }}>
                    Ours: {ours.label}
                </div>
            )}

            {compare === 'direction' && (
                <div style={{ marginTop: 6, display: 'flex', gap: 16 }}>
                    <span style={{ fontSize: 12, color: COLORS.textMuted }}>
                        Field <strong style={{ color: COLORS.text }}>{fieldChange}%</strong>
                    </span>
                    <span style={{ fontSize: 12, color: COLORS.textMuted }}>
                        Ours{' '}
                        <strong style={{ color: behind ? COLORS.warning : COLORS.success }}>{ourChange}%</strong>
                    </span>
                    {behind !== null && (
                        <span style={{ fontSize: 11.5, color: behind ? COLORS.warning : COLORS.success }}>
                            {behind ? 'behind the field' : 'ahead of the field'}
                        </span>
                    )}
                </div>
            )}

            {note && !compact && (
                <p style={{ fontSize: 11.5, color: COLORS.textMuted, lineHeight: 1.55, margin: '8px 0 0' }}>
                    {note}
                </p>
            )}

            {(source || retrieved) && (
                <div style={{ fontSize: 10.5, color: COLORS.textMuted, marginTop: 6 }}>
                    {source}
                    {retrieved ? ` · retrieved ${retrieved}` : ''}
                </div>
            )}
        </div>
    )
}
